import 'preline/preline';
import { useContext, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import AdminDashboard from './components/AdminDashboard';
import AlumniFinder from './components/AlumniFinder/AlumniFinder';
import AppTracker from './components/ApplicationTracker/AppTracker';
import BehavioralPrep from './components/BehavioralPrep/BehavioralPrep';
import DailyDigest from './components/DailyDigest/DailyDigest';
import Footer from './components/Footer';
import JobBoard from './components/JobBoard/JobBoard';
import LandingPage from './components/LandingPage/LandingPage';
import LessonInterface from './components/Lesson/Lesson';
import Navbar from './components/Navbar';
import PageAccessWrapper from './components/PageAccessWrapper';
import ProgressTree from './components/ProgressTree/ProgressTree';
import ProtectedRoute from './components/ProtectedRoute';
import RedirectToHtml from './components/RedirectToHtml';
import LeetcodeTracker from './components/Technical/LeetcodeTracker';
import MockInterview from './components/Technical/MockInterview';
import Toast from './components/Toast';
import { useAuth } from './contexts/AuthContext';
import { GlobalContext } from './contexts/GlobalContext';
// Import consolidated auth components
import AuthPage from './components/Auth/AuthPage';
import CheckEmail from './components/Auth/CheckEmail';
import CompleteRegistration from './components/Auth/CompleteRegistration';
import ForgotPassword from './components/Auth/ForgotPassword';
import OpenRegistration from './components/Auth/OpenRegistration';
import ResetPassword from './components/Auth/ResetPassword';
import VerifyEmail from './components/Auth/VerifyEmail';
import Contact from './components/Contact';
import AcceptQuestModal from './components/Quests/AcceptQuestModal';
// Conditionally import DebugAuth only in development
const DebugAuth =
  process.env.NODE_ENV === 'development' ? require('./components/DebugAuth').default : null;

function App() {
  const { activeTab, studentView, showToast, toastMessage, hideToast } = useContext(GlobalContext);
  const { isAuthenticated, loading } = useAuth();
  const { hasOnboarded } = useContext(GlobalContext);
  const [isMobile, setIsMobile] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]); // Runs every time the route changes

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  });

  // Redirect to /progress-tree if authenticated but not onboarded
  useEffect(() => {
    if (isAuthenticated && !hasOnboarded && location.pathname !== '/progress-tree') {
      navigate('/progress-tree', { replace: true });
    }
  }, [isAuthenticated, hasOnboarded, location.pathname, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Show mobile device blocking screen
  if (isMobile) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center p-8 max-w-md mx-4 bg-white">
          <div className="animate-bounce mb-6">
            <img
              src="/badges/kitty_sweating.png"
              alt="Sweating Kitty"
              className="w-32 h-32 mx-auto"
            />
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-4">
            Nooo! InternHub can't be used on mobile
          </h1>
          <p className="text-gray-600 mb-6">
            Don't let your phone get in the way of your next internship! This is your sign to get up
            and grind :)
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 whitespace-pre-line min-h-screen flex flex-col prevent-overflow">
      {/* Debug component - remove in production */}
      {DebugAuth && <DebugAuth />}

      {isAuthenticated && <Navbar />}
      <div className={`${isAuthenticated ? 'pt-16' : ''} grow flex flex-col`}>
        <Routes>
          {!isAuthenticated ? (
            <>
              <Route path="/login" element={<AuthPage />} />
              <Route path="/register" element={<AuthPage />} />
              <Route path="/register/:schoolCode" element={<AuthPage />} />
              <Route path="/free-trial" element={<OpenRegistration />} />
              <Route path="/" element={<LandingPage />} />
              <Route path="/check-email" element={<CheckEmail />} />
              <Route path="/complete-registration" element={<CompleteRegistration />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route
                path="/our-cookie-policy"
                element={<RedirectToHtml htmlPath="/our-cookie-policy.html" />}
              />
              <Route
                path="/our-privacy-policy"
                element={<RedirectToHtml htmlPath="/our-privacy-policy.html" />}
              />
              <Route
                path="/our-terms-of-service"
                element={<RedirectToHtml htmlPath="/our-terms-of-service.html" />}
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </>
          ) : studentView ? (
            <>
              <Route path="/admin-dashboard" element={<AdminDashboard />} />
              <Route path="/accept-quest" element={<AcceptQuestModal />} />
              <Route
                path="/home"
                element={
                  <ProtectedRoute>
                    <PageAccessWrapper
                      Component={DailyDigest}
                      requiredLesson="start"
                      lessonName="Learn about InternHub"
                      lessonPath="/lesson/start"
                    />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/application-tracking"
                element={
                  <ProtectedRoute>
                    <PageAccessWrapper
                      Component={AppTracker}
                      requiredLesson="applying"
                      lessonName="The Perfect Application"
                      lessonPath="/lesson/applying"
                    />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/technical"
                element={
                  <ProtectedRoute>
                    <PageAccessWrapper
                      Component={LeetcodeTracker}
                      requiredLesson="leetcode"
                      lessonName="Leetcode"
                      lessonPath="/lesson/leetcode"
                    />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/technical/mock-interview/:problemName"
                element={
                  <ProtectedRoute>
                    <PageAccessWrapper
                      Component={MockInterview}
                      requiredLesson="leetcode"
                      lessonName="Leetcode"
                      lessonPath="/lesson/leetcode"
                    />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/behavioral-interview"
                element={
                  <ProtectedRoute>
                    <PageAccessWrapper
                      Component={BehavioralPrep}
                      requiredLesson="behavioral"
                      lessonName="Behavioral"
                      lessonPath="/lesson/behavioral"
                    />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/progress-tree"
                element={
                  <ProtectedRoute>
                    <ProgressTree />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/job-board"
                element={
                  <ProtectedRoute>
                    <PageAccessWrapper
                      Component={JobBoard}
                      requiredLesson="roles"
                      lessonName="Roles & Positions"
                      lessonPath="/lesson/roles"
                    />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/alumni-finder"
                element={
                  <ProtectedRoute>
                    <PageAccessWrapper
                      Component={AlumniFinder}
                      requiredLesson="networking"
                      lessonName="Networking"
                      lessonPath="/lesson/networking"
                    />
                  </ProtectedRoute>
                }
              />
              {/* Lesson routes - these don't need the PageAccessWrapper */}
              <Route
                path="/lesson/start"
                element={
                  <ProtectedRoute>
                    <LessonInterface route="start" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/lesson/roles"
                element={
                  <ProtectedRoute>
                    <LessonInterface route="roles" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/lesson/process"
                element={
                  <ProtectedRoute>
                    <LessonInterface route="process" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/lesson/applying"
                element={
                  <ProtectedRoute>
                    <LessonInterface route="applying" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/lesson/resume"
                element={
                  <ProtectedRoute>
                    <LessonInterface route="resume" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/lesson/interviews"
                element={
                  <ProtectedRoute>
                    <LessonInterface route="interviews" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/lesson/technical"
                element={
                  <ProtectedRoute>
                    <LessonInterface route="technical" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/lesson/behavioral"
                element={
                  <ProtectedRoute>
                    <LessonInterface route="behavioral" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/lesson/alternative"
                element={
                  <ProtectedRoute>
                    <LessonInterface route="alternative" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/lesson/networking"
                element={
                  <ProtectedRoute>
                    <LessonInterface route="networking" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/lesson/leetcode"
                element={
                  <ProtectedRoute>
                    <LessonInterface route="leetcode" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/lesson/ocs"
                element={
                  <ProtectedRoute>
                    <LessonInterface route="ocs" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/lesson/linkedin"
                element={
                  <ProtectedRoute>
                    <LessonInterface route="linkedin" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/lesson/phone"
                element={
                  <ProtectedRoute>
                    <LessonInterface route="phone" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/lesson/clubs"
                element={
                  <ProtectedRoute>
                    <LessonInterface route="clubs" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/lesson/conference"
                element={
                  <ProtectedRoute>
                    <LessonInterface route="conference" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/contact"
                element={
                  <ProtectedRoute>
                    <Contact />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/our-cookie-policy"
                element={<RedirectToHtml htmlPath="/our-cookie-policy.html" />}
              />
              <Route
                path="/our-privacy-policy"
                element={<RedirectToHtml htmlPath="/our-privacy-policy.html" />}
              />
              <Route
                path="/our-terms-of-service"
                element={<RedirectToHtml htmlPath="/our-terms-of-service.html" />}
              />
              <Route path="/" element={<Navigate to="/home" replace />} />
              <Route path="*" element={<Navigate to="/home" replace />} />
            </>
          ) : (
            <>
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/contact"
                element={
                  <ProtectedRoute>
                    <Contact />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/our-cookie-policy"
                element={<RedirectToHtml htmlPath="/our-cookie-policy.html" />}
              />
              <Route
                path="/our-privacy-policy"
                element={<RedirectToHtml htmlPath="/our-privacy-policy.html" />}
              />
              <Route
                path="/our-terms-of-service"
                element={<RedirectToHtml htmlPath="/our-terms-of-service.html" />}
              />
              <Route path="/" element={<Navigate to="/" replace />} />
            </>
          )}
        </Routes>
      </div>
      <Toast message={toastMessage} isVisible={showToast} onClose={hideToast} />
      {isAuthenticated && <Footer />}
    </div>
  );
}

export default App;
